import { useEffect, useRef } from "react";
import {
  overlayJointsForSample,
  COCO_BONE_PAIRS,
  MEDIAPIPE_POSE_BONE_PAIRS,
  sampleForPlayback,
} from "../../utils/poseTimeline";
import { roiFromJoints } from "../../utils/playerRoiFromJoints";
import {
  estimateTrunkRotation,
  trunkRotationDegForOverlay,
} from "../../utils/trunkRotation";
import { containRect } from "../../utils/videoContainCoords";
import { subscribePresentedVideoTime } from "../../utils/videoFrameDuration";
import "./poseOverlays.css";

/** Match iOS `UIColor.cyan` / `systemPink` on the 2D pose overlay. */
export const LEFT_COLOR = "#00FFFF";
export const RIGHT_COLOR = "#FF2D55";
const PREDICTED_JOINT_COLOR = "#FF9500";
const TABLE_HIGHLIGHT_STROKE = "#FFE566";
const TABLE_HIGHLIGHT_FILL = "rgba(0, 255, 255, 0.2)";

export const TABLE_HIGHLIGHT_MS = 5000;

export function skeletonBoneWidth(rect) {
  return Math.max(2, Math.min(rect.w, rect.h) / 220);
}

export function skeletonJointRadius(rect) {
  return Math.max(2.5, Math.min(rect.w, rect.h) / 160);
}

function isPredicted(predicted, i) {
  return Array.isArray(predicted) && predicted[i] === true;
}

function isArmJoint(i) {
  return i === 7 || i === 8 || i === 9 || i === 10;
}

function isElbowWristBone(a, b) {
  return (
    (a === 7 && b === 9) ||
    (a === 9 && b === 7) ||
    (a === 8 && b === 10) ||
    (a === 10 && b === 8)
  );
}

function isShoulderElbowBone(a, b) {
  return (
    (a === 5 && b === 7) ||
    (a === 7 && b === 5) ||
    (a === 6 && b === 8) ||
    (a === 8 && b === 6)
  );
}

function rectValid(r) {
  return r && Number(r.w) >= 0.02 && Number(r.h) >= 0.02;
}

function tableHighlightOpacity(untilMs, nowMs) {
  if (!untilMs || nowMs >= untilMs) return 0;
  const remaining = untilMs - nowMs;
  const elapsed = TABLE_HIGHLIGHT_MS - remaining;
  const fadeStart = 1500;
  if (elapsed <= fadeStart) return 1;
  return Math.max(0, 1 - (elapsed - fadeStart) / (TABLE_HIGHLIGHT_MS - fadeStart));
}

/**
 * Draw detection ROI box (auto-detect / tracked crop around player joints).
 */
function drawRoiBox(ctx, roi, color, rect, label) {
  if (!rectValid(roi) || !rect) return;
  const { x: ox, y: oy, w, h } = rect;
  const x = ox + Number(roi.x) * w;
  const y = oy + Number(roi.y) * h;
  const bw = Number(roi.w) * w;
  const bh = Number(roi.h) * h;
  const strokeW = Math.max(1.5, Math.min(w, h) * 0.003);
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.fillStyle = `${color}18`;
  ctx.strokeStyle = color;
  ctx.lineWidth = strokeW;
  ctx.setLineDash([strokeW * 4, strokeW * 2.5]);
  ctx.fillRect(x, y, bw, bh);
  ctx.strokeRect(x, y, bw, bh);
  ctx.setLineDash([]);
  if (label) {
    const fontSize = Math.max(11, Math.min(w, h) * 0.028);
    ctx.font = `700 ${fontSize}px sans-serif`;
    ctx.fillStyle = color;
    ctx.textBaseline = "bottom";
    const ty = Math.max(oy + fontSize + 2, y - 4);
    ctx.fillText(label, x + strokeW, ty);
  }
  ctx.restore();
}

/**
 * Prefer per-sample tracked ROI, else joints bbox (same as auto-detect seed).
 * When pipeline emits sample.roi_left/right (including held-on-miss boxes),
 * callers must prefer those over this helper's joints/seed fallback.
 */
function resolvePlayerRoi(sideSample, seedRoi) {
  const tracked = sideSample?.roi || sideSample?.roi_box || null;
  if (rectValid(tracked)) return tracked;
  const fromJoints = roiFromJoints(sideSample?.joints);
  if (rectValid(fromJoints)) return fromJoints;
  if (rectValid(seedRoi)) return seedRoi;
  return null;
}

function strokeBone(ctx, x1, y1, x2, y2, color, lineWidth, predicted) {
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  if (predicted) ctx.setLineDash([6, 4]);

  ctx.globalAlpha = predicted ? 0.18 : 0.28;
  ctx.lineWidth = lineWidth * 2.4;
  ctx.shadowColor = color;
  ctx.shadowBlur = lineWidth * 4;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.globalAlpha = predicted ? 0.55 : 0.95;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw every detected joint. MediaPipe 33 uses hand/face/foot bones; COCO-17
 * keeps the older predicted-arm coloring for cached Kalman results.
 * Visuals match iOS PoseOverlayRenderer (cyan/pink, round caps) plus a soft
 * outer stroke so the overlay reads the same on a CSS-transformed canvas.
 */
function drawPlayer(
  ctx,
  joints,
  color,
  rect,
  predicted = null,
  bonePairs = null,
  isPlayerA = true
) {
  if (!Array.isArray(joints) || joints.length < 5) return;
  const bones =
    Array.isArray(bonePairs) && bonePairs.length
      ? bonePairs
      : joints.length >= 33
        ? MEDIAPIPE_POSE_BONE_PAIRS
        : COCO_BONE_PAIRS;
  const cocoArmStyle = joints.length < 33;
  const { x: ox, y: oy, w, h } = rect;
  const lineWidth = skeletonBoneWidth(rect);

  const usable = (j, i) => {
    if (!j) return false;
    if ((j.score ?? 0) < 0.12) return false;
    if (cocoArmStyle && isPredicted(predicted, i) && !isArmJoint(i)) return false;
    return true;
  };
  const isBioArm = (i) => cocoArmStyle && isArmJoint(i) && isPredicted(predicted, i);

  const boneIsPredicted = (a, b) => {
    if (!cocoArmStyle) return false;
    if (isElbowWristBone(a, b)) {
      return isBioArm(a) || isBioArm(b);
    }
    if (isShoulderElbowBone(a, b)) {
      return (isBioArm(7) && (a === 7 || b === 7))
        || (isBioArm(8) && (a === 8 || b === 8));
    }
    return false;
  };

  const boneAllowed = (a, b) => {
    if (!cocoArmStyle) return true;
    if (isElbowWristBone(a, b)) {
      const wrist = a === 9 || b === 9 ? 9 : 10;
      return usable(joints[wrist], wrist);
    }
    return true;
  };

  for (const [a, b] of bones) {
    if (!boneAllowed(a, b)) continue;
    const ja = joints[a];
    const jb = joints[b];
    if (!usable(ja, a) || !usable(jb, b)) continue;
    strokeBone(
      ctx,
      ox + ja.x * w,
      oy + ja.y * h,
      ox + jb.x * w,
      oy + jb.y * h,
      color,
      lineWidth,
      boneIsPredicted(a, b)
    );
  }

  const r = skeletonJointRadius(rect);
  for (let i = 0; i < joints.length; i++) {
    const j = joints[i];
    if (!usable(j, i)) continue;
    const pred = isBioArm(i);
    ctx.save();
    ctx.fillStyle = pred
      ? PREDICTED_JOINT_COLOR
      : isPlayerA
        ? "rgba(255,255,255,0.9)"
        : color;
    ctx.globalAlpha = pred ? 0.85 : 0.9;
    ctx.shadowColor = pred ? PREDICTED_JOINT_COLOR : color;
    ctx.shadowBlur = r * 1.8;
    ctx.beginPath();
    ctx.arc(ox + j.x * w, oy + j.y * h, pred ? r * 1.08 : r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawTableHighlight(ctx, corners, rect, opacity) {
  if (!Array.isArray(corners) || corners.length !== 4 || opacity <= 0) return;
  const { x: ox, y: oy, w, h } = rect;
  const pts = corners.map((c) => ({
    x: ox + c.x * w,
    y: oy + c.y * h,
  }));
  const lineWidth = Math.max(2.5, Math.min(w, h) / 180);

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.beginPath();
  pts.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.closePath();
  ctx.fillStyle = TABLE_HIGHLIGHT_FILL;
  ctx.fill();
  ctx.strokeStyle = TABLE_HIGHLIGHT_STROKE;
  ctx.lineWidth = lineWidth * 2.6;
  ctx.shadowColor = "rgba(0, 255, 255, 0.95)";
  ctx.shadowBlur = lineWidth * 8;
  ctx.stroke();
  ctx.shadowBlur = lineWidth * 3;
  ctx.strokeStyle = "#00FFFF";
  ctx.lineWidth = lineWidth;
  ctx.globalAlpha = opacity * 0.95;
  ctx.stroke();
  ctx.restore();
}

/**
 * Emphasize the shoulder girdle and label trunk yaw vs the nearer table end line.
 */
function drawTrunkRotation(ctx, joints, color, rect, tableCorners) {
  const est = estimateTrunkRotation(joints, { tableCorners });
  const deg = trunkRotationDegForOverlay(est);
  if (
    deg == null ||
    !est.leftShoulder ||
    !est.rightShoulder ||
    !est.midShoulder
  ) {
    return est.shoulderSpan;
  }

  const { x: ox, y: oy, w, h } = rect;
  const toPx = (p) => ({ x: ox + p.x * w, y: oy + p.y * h });
  const ls = toPx(est.leftShoulder);
  const rs = toPx(est.rightShoulder);
  const mid = toPx(est.midShoulder);

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.95;
  ctx.lineWidth = Math.max(2, Math.min(w, h) * 0.0035);
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(ls.x, ls.y);
  ctx.lineTo(rs.x, rs.y);
  ctx.stroke();

  if (est.leftHip && est.rightHip) {
    const lh = toPx(est.leftHip);
    const rh = toPx(est.rightHip);
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = Math.max(1.2, Math.min(w, h) * 0.002);
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(lh.x, lh.y);
    ctx.lineTo(rh.x, rh.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  const fontSize = Math.max(12, Math.min(w, h) * 0.032);
  const label = `Trunk ${Math.round(deg)}°`;
  ctx.globalAlpha = 1;
  ctx.font = `700 ${fontSize}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  const lx = mid.x;
  const ly = mid.y - Math.max(6, fontSize * 0.35);
  ctx.lineWidth = Math.max(3, fontSize * 0.18);
  ctx.strokeStyle = "rgba(0,0,0,0.75)";
  ctx.strokeText(label, lx, ly);
  ctx.fillStyle = color;
  ctx.fillText(label, lx, ly);
  ctx.restore();
}

/**
 * Canvas overlay aligned to the video's object-fit:contain content box.
 * Parent should share the same CSS flip/rotate as the <video> element.
 */
export function PoseSkeletonOverlay({
  sample,
  /** Full pose timeline; with videoRef, pick sample per presented frame. */
  samples = null,
  /** HTMLVideoElement ref — sync skeleton to source fps without React re-renders. */
  videoRef = null,
  analysisFps = null,
  tableCorners,
  videoWidth = 0,
  videoHeight = 0,
  visible = true,
  showSkeleton = true,
  showRoiBoxes = false,
  showTrunkRotation = false,
  /** Epoch ms; table quad glows until this time, then stays hidden. */
  tableHighlightUntil = 0,
  shoulderWidthRef = null,
  /** Seed ROIs from calibration / auto-detect (element space). */
  seedRoiLeft = null,
  seedRoiRight = null,
  /** When true, paint right-player color on the left sample after mirror UX. Unused — CSS flip handles geometry. */
  swapSides = false,
}) {
  const canvasRef = useRef(null);
  const sampleRef = useRef(sample);
  sampleRef.current = sample;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !visible) return undefined;

    let glowRaf = 0;
    let unsubFrame = null;

    const resolveSample = (mediaTimeSec) => {
      const list = Array.isArray(samples) ? samples : null;
      if (list?.length && Number.isFinite(mediaTimeSec)) {
        const fps =
          Number.isFinite(Number(analysisFps)) && Number(analysisFps) > 1
            ? Number(analysisFps)
            : null;
        const fd = fps ? 1 / fps : null;
        const frameIndex =
          fd > 0 ? Math.max(0, Math.floor(mediaTimeSec / fd + 1e-6)) : null;
        return (
          sampleForPlayback(list, {
            tMs: Math.round(mediaTimeSec * 1000),
            frameIndex,
            padMs: 250,
          }) || sampleRef.current
        );
      }
      return sampleRef.current;
    };

    const paint = (mediaTimeSec = null) => {
      if (glowRaf) {
        cancelAnimationFrame(glowRaf);
        glowRaf = 0;
      }
      const cssW = Math.max(1, canvas.clientWidth || 1);
      const cssH = Math.max(1, canvas.clientHeight || 1);
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const bufW = Math.round(cssW * dpr);
      const bufH = Math.round(cssH * dpr);
      if (canvas.width !== bufW || canvas.height !== bufH) {
        canvas.width = bufW;
        canvas.height = bufH;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      const rect = containRect(cssW, cssH, videoWidth, videoHeight);
      const glowOpacity = tableHighlightOpacity(tableHighlightUntil, Date.now());
      if (tableCorners && glowOpacity > 0) {
        drawTableHighlight(ctx, tableCorners, rect, glowOpacity);
      }

      const active = resolveSample(mediaTimeSec);
      const leftSample = swapSides ? active?.right : active?.left;
      const rightSample = swapSides ? active?.left : active?.right;
      const seedL = swapSides ? seedRoiRight : seedRoiLeft;
      const seedR = swapSides ? seedRoiLeft : seedRoiRight;

      if (showRoiBoxes) {
        const sampleLeftRoi = active?.roi_left || null;
        const sampleRightRoi = active?.roi_right || null;
        const leftRoi =
          (rectValid(sampleLeftRoi) ? sampleLeftRoi : null) ||
          resolvePlayerRoi(leftSample, seedL);
        const rightRoi =
          (rectValid(sampleRightRoi) ? sampleRightRoi : null) ||
          resolvePlayerRoi(rightSample, seedR);
        drawRoiBox(ctx, leftRoi, LEFT_COLOR, rect, "A / G");
        drawRoiBox(ctx, rightRoi, RIGHT_COLOR, rect, "B / D");
      }

      if (showSkeleton) {
        for (const [sideSample, color, isPlayerA] of [
          [leftSample, LEFT_COLOR, true],
          [rightSample, RIGHT_COLOR, false],
        ]) {
          const overlay = overlayJointsForSample(sideSample);
          if (overlay.joints) {
            drawPlayer(
              ctx,
              overlay.joints,
              color,
              rect,
              overlay.predicted,
              overlay.bones,
              isPlayerA
            );
            if (showTrunkRotation) {
              drawTrunkRotation(
                ctx,
                overlay.joints,
                color,
                rect,
                tableCorners
              );
            }
          }
        }
      }

      if (glowOpacity > 0 && !unsubFrame) {
        glowRaf = requestAnimationFrame(() => paint(mediaTimeSec));
      }
    };

    const video = videoRef?.current;
    if (video && Array.isArray(samples) && samples.length > 0) {
      const paintNow = () => paint(video.currentTime || 0);
      paintNow();
      const onSeeked = () => paintNow();
      const onPause = () => paintNow();
      video.addEventListener("seeked", onSeeked);
      video.addEventListener("pause", onPause);
      unsubFrame = subscribePresentedVideoTime(video, (t) => {
        if (video.paused) return;
        paint(t);
      });
      const ro = new ResizeObserver(paintNow);
      ro.observe(canvas);
      return () => {
        ro.disconnect();
        video.removeEventListener("seeked", onSeeked);
        video.removeEventListener("pause", onPause);
        unsubFrame?.();
        unsubFrame = null;
        if (glowRaf) cancelAnimationFrame(glowRaf);
      };
    }

    paint();
    const ro = new ResizeObserver(() => paint());
    ro.observe(canvas);
    return () => {
      ro.disconnect();
      if (glowRaf) cancelAnimationFrame(glowRaf);
    };
    // Intentionally omit `sample` when samples+videoRef drive paint — parent
    // timeupdate would otherwise restart the frame subscription ~4×/s.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sample via sampleRef
  }, [
    samples,
    videoRef,
    analysisFps,
    tableCorners,
    videoWidth,
    videoHeight,
    visible,
    showSkeleton,
    showRoiBoxes,
    showTrunkRotation,
    tableHighlightUntil,
    shoulderWidthRef,
    seedRoiLeft,
    seedRoiRight,
    swapSides,
    Array.isArray(samples) && samples.length ? null : sample,
  ]);

  if (!visible) return null;

  return (
    <canvas
      ref={canvasRef}
      className="pose-skeleton-overlay"
      aria-hidden
    />
  );
}

export default PoseSkeletonOverlay;
